import React from 'react';
import PropTypes from 'prop-types';
import UIDateFormater, {
  DateType,
} from '../../../containers/UIhelper/UIDateFormater';
import SensorInfoAdvanced from './SensorInfoAdvanced';
import Plot from 'react-plotly.js';
import { useDispatch, useSelector } from 'react-redux';
import { selectSensorSample } from '../../../actions/SensorsActions';
import IntensityNoiseGraph from '../IntensityNoiseGraph';
import useProjectTimezone from '../../../hooks/useProjectTimeZone';
import PressureSensorSampleStrip from './PressureSensorSampleStrip';

require('./SensorInfo.scss');

const graphWidth = 330;
const graphHeigh = 220;
const markerSize = 7;
const MAX_WAV_VALUE = 32768;
const WAV_HEADER_SIZE = 44;
const WAV_SAMPLE_RATE_OFFSET = 24;
const WAV_CHANNELS_OFFSET = 22;
const AUDIO_SIGNAL_TRIM_MS = 50;

const getWavDataChunk = (view) => {
  let offset = 12;

  while (offset + 8 <= view.byteLength) {
    const chunkId = String.fromCharCode(
      view.getUint8(offset),
      view.getUint8(offset + 1),
      view.getUint8(offset + 2),
      view.getUint8(offset + 3)
    );
    const chunkSize = view.getUint32(offset + 4, true);

    if (chunkId === 'data') {
      return {
        offset: offset + 8,
        size: chunkSize,
      };
    }

    offset += 8 + chunkSize + (chunkSize % 2);
  }

  return undefined;
};

const getWavBytes = (url) =>
  new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          return reject(
            new Error(`HTTP ${response.status}: ${response.statusText}`)
          );
        }
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const view = new DataView(buffer);
        const sampleRate = view.getUint32(WAV_SAMPLE_RATE_OFFSET, true);
        const channels = view.getUint16(WAV_CHANNELS_OFFSET, true);
        const dataChunk = getWavDataChunk(view);

        if (!dataChunk) {
          return reject(new Error('No WAV data chunk found'));
        }

        const trimSamples = Math.round(
          sampleRate * channels * (AUDIO_SIGNAL_TRIM_MS / 1000)
        );

        const data = Array.from(
          new Int16Array(buffer, dataChunk.offset, dataChunk.size / 2)
        ).slice(trimSamples);
        resolve(data);
      })
      .catch((error) => {
        reject(error);
      });
  });

const SensorInfo = (props, context) => {
  const { sensorDetails, user } = props;

  // IMPORTANT: All hooks must be called unconditionally to comply with React's Rules of Hooks
  // We cannot use early returns before hooks, even conditionally, because React requires
  // the same number of hooks to be called on every render of the same component instance.
  const dispatch = useDispatch();
  const projectTimezone = useProjectTimezone();
  const [input, setInput] = React.useState(props.noiseAlertCount || 10);
  const [nc1Data, setNc1Data] = React.useState([]);
  const [nc2Data, setNc2Data] = React.useState([]);
  const [nc3Data, setNc3Data] = React.useState([]);
  const [times, setTimes] = React.useState([]);
  const [xValues, setXValues] = React.useState([]);
  const [audioSignal, setAudioSignal] = React.useState();

  const project = useSelector((state) => state.leaksList?.selectedProject);
  const sensors = useSelector(
    (state) => state.leaksList.leaksByProject?.[project]?.sensors || {}
  );
  const timeZone = useSelector((state) => state.local.timeZone);
  const selectedSensorSample = useSelector(
    (state) => state.leaksList.leaksByProject?.selectedSensorSample || {}
  );

  const noiseGraphData = sensors?.g5NoiseGraphData;
  const intensitySampleTime = useSelector(
    (state) => state.leaksList.leaksByProject.selectedSensorSample.SampleTime
  );

  const DeviceGenerationValue = useSelector((state) => {
    let value =
      state.leaksList.leaksByProject[project]?.sensors?.details
        ?.DeviceGenerationValue;
    if (value === undefined || value === null) {
      const selectedSensorID =
        state.leaksList.leaksByProject?.selectedFeature ||
        state.noiseAlerts.selected?.SensorID;
      const sensorsArray =
        state.leaksList.leaksByProject[project]?.sensors?.sensors;
      const sensor = sensorsArray?.find(
        (sensor) => sensor.id === selectedSensorID
      );
      value = sensor?.DeviceGenerationValue ?? null;
    }
    return value;
  });

  React.useEffect(() => {
    if (props.sensorDetails?.details) {
      const samplesLength = props.sensorDetails.details.length;
      const nc1Array = [];
      const nc2Array = [];
      const nc3Array = [];
      const timesArray = [];
      const xValuesArr = [];

      props.sensorDetails.details.forEach((sample, index) => {
        nc1Array.push(sample.Nc1);
        nc2Array.push(sample.Nc2);
        nc3Array.push(sample.Nc3);
        timesArray.push(
          UIDateFormater(
            sample.SampleTime,
            DateType.DATE_AND_TIME,
            props.timeZone,
            projectTimezone
          )
        );
        xValuesArr.push(samplesLength - index);
      });

      // Always create new arrays to ensure React detects changes
      setNc1Data([...nc1Array]);
      setNc2Data([...nc2Array]);
      setNc3Data([...nc3Array]);
      setTimes([...timesArray]);
      setXValues([...xValuesArr]);
    }
  }, [props.sensorDetails, selectedSensorSample?.ID]);

  React.useEffect(() => {
    if (props.sampleAudio) {
      getWavBytes(props.sampleAudio)
        .then(setAudioSignal)
        .catch(() => {
          setAudioSignal(undefined);
        });
    } else {
      setAudioSignal(undefined);
    }
    return () => {
      setAudioSignal(undefined);
    };
  }, [props.sampleAudio]);

  const handleChange = (event) => {
    const value = Math.min(event.target.value, 60);
    setInput(value);
  };

  const reloadNoiseSamples = () => {
    props.reloadNoiseSamples(input);
  };

  const renderAudioElement = (audioUrl) => (
    <audio className='grid-item' src={audioUrl} controls />
  );

  const hoverTemplate = '%{y:d}<br>%{text}<extra></extra>';

  // Create trace objects with new references when selectedSensorSample changes
  // This ensures Plotly detects the change and updates the graphs
  const filterTrace = React.useMemo(
    () => ({
      x: [...xValues],
      y: [...nc3Data],
      text: [...times],
      mode: 'lines+markers',
      type: 'scatter',
      name: 'Filtered Intensity',
      hovertemplate: hoverTemplate,
      textposition: 'top center',
      line: {
        color: 'rgb(255, 127, 14)',
      },
      marker: {
        color: 'rgba(255, 255, 255, 100)',
        size: markerSize,
        line: {
          color: 'rgb(255, 127, 14)',
          width: 1,
        },
      },
    }),
    [xValues, nc3Data, times, selectedSensorSample?.ID]
  );

  const nc1Trace = React.useMemo(
    () => ({
      x: [...xValues],
      y: [...nc1Data],
      text: [...times],
      mode: 'lines+markers',
      type: 'scatter',
      name: 'Minimal',
      hovertemplate: hoverTemplate,
      textposition: 'top center',
      line: {
        color: 'rgb(0, 7, 255)',
      },
      marker: {
        color: 'rgba(255, 255, 255, 100)',
        size: markerSize,
        line: {
          color: 'rgb(0, 7, 255)',
          width: 1,
        },
      },
    }),
    [xValues, nc1Data, times, selectedSensorSample?.ID]
  );

  const nc2Trace = React.useMemo(
    () => ({
      x: [...xValues],
      y: [...nc2Data],
      text: [...times],
      mode: 'lines+markers',
      type: 'scatter',
      name: 'Sampled',
      hovertemplate: hoverTemplate,
      textposition: 'top center',
      line: {
        color: 'rgb(255, 0, 0)',
      },
      marker: {
        color: 'rgba(255, 255, 255, 100)',
        size: markerSize,
        line: {
          color: 'rgb(255, 0, 0)',
          width: 1,
        },
      },
    }),
    [xValues, nc2Data, times, selectedSensorSample?.ID]
  );

  const freqColorsMap = {
    300: 'rgb(20, 190, 40)',
    600: 'rgb(33,171,235)',
    1200: 'rgb(255, 127, 14)',
  };

  const freqScatter = React.useMemo(() => {
    if (!props.sensorDetails.nc3Bars) return [];
    return [
      { original: '300', label: 'low' },
      { original: '600', label: 'med' },
      { original: '1200', label: 'high freq.' },
    ].map(({ original, label }) => ({
      name: label,
      x: [...xValues],
      y: props.sensorDetails.nc3Bars.map((x) => x[original]),
      type: 'scatter',
      text: [...times],
      hovertemplate: hoverTemplate,
      mode: 'lines+markers',
      line: {
        color: freqColorsMap[original],
      },
      marker: {
        color: 'rgba(255, 255, 255, 1)',
        size: markerSize,
        line: {
          color: freqColorsMap[original],
          width: 1,
        },
      },
    }));
  }, [xValues, times, props.sensorDetails.nc3Bars, selectedSensorSample?.ID]);

  const plotLayout = {
    showlegend: true,
    showdividers: false,
    width: graphWidth,
    height: graphHeigh,
    yaxis: {
      rangemode: 'tozero',
      title: context.t('intensity'),
    },
    legend: { orientation: 'h' },
    margin: {
      l: 50,
      r: 10,
      b: 40,
      t: 30,
    },
    hoverlabel: {
      bgcolor: 'white',
      font: {
        size: 16,
      },
    },
  };

  const handlePlotMarkerClick = (point) => {
    const index = point.x - 1;
    const item = props.sensorDetails.details.at(-index - 1);
    dispatch(selectSensorSample(props.project, item));
  };

  const isG5Sensor = DeviceGenerationValue === 1;
  const SHOW_NOISE_GRAPH =
    JSON.parse(process.env.REACT_APP_SHOW_NC1_NOISE_GRAPH) || false;

  // Determine if this is a pressure sensor sample
  // Pressure sensors have barsData but no details array
  const isPressureSensorSample =
    sensorDetails?.barsData != null &&
    Array.isArray(sensorDetails.barsData) &&
    sensorDetails.barsData.length === 3 &&
    (!sensorDetails.details || sensorDetails.details.length === 0);

  const closePanel = () => {
    props.handleClosePannel('snesorSample');
  };

  // All hooks have been called - now we can do conditional returns
  if (isPressureSensorSample) {
    return (
      <PressureSensorSampleStrip
        sensorDetails={sensorDetails}
        onClose={closePanel}
        context={context}
        samplesCount={20}
        reloadSamples={props.reloadNoiseSamples}
      />
    );
  }

  if (
    sensorDetails.nc1Array == null ||
    (sensorDetails.nc1Array.length === 0 && sensorDetails.nc2Array == null) ||
    (sensorDetails.nc2Array.length === 0 && sensorDetails.nc3Array == null) ||
    (sensorDetails.nc3Array.length === 0 &&
      sensorDetails.nc3aveArray == null) ||
    (sensorDetails.nc3aveArray.length === 0 && sensorDetails.barsData == null)
  ) {
    return null;
  }

  return (
    <div className='sample-info'>
      {user.analysesExpert && <SensorInfoAdvanced project={props.project} />}
      <div>
        <i className='icon-close' onClick={closePanel} />
        <div className='reload-noises-parent'>
          <label>{context.t('number_of_samples')}:</label>
          <input
            className='input-field'
            type='number'
            placeholder={context.t('number_of_samples')}
            min='0'
            max='60'
            onChange={handleChange}
            value={input}
          />
          <button className='btn-reload-noises' onClick={reloadNoiseSamples}>
            {context.t('load')}
          </button>
        </div>

        <div className='sample-graphs'>
          {SHOW_NOISE_GRAPH && isG5Sensor ? (
            <IntensityNoiseGraph
              key={`noise-${selectedSensorSample?.ID || 'none'}`}
              data={noiseGraphData}
              sampleTime={intensitySampleTime}
              timeZone={timeZone}
            />
          ) : (
            filterTrace && (
              <Plot
                key={`filter-${selectedSensorSample?.ID || 'none'}`}
                data={[filterTrace]}
                layout={plotLayout}
                onClick={(e) => {
                  handlePlotMarkerClick(e.points[0]);
                }}
              />
            )
          )}

          {nc1Trace && nc2Trace && (
            <Plot
              key={`nc1nc2-${selectedSensorSample?.ID || 'none'}`}
              data={[nc1Trace, nc2Trace]}
              layout={plotLayout}
              onClick={(e) => {
                handlePlotMarkerClick(e.points[0]);
              }}
            />
          )}

          {freqScatter && freqScatter.length > 0 && (
            <Plot
              key={`freq-${selectedSensorSample?.ID || 'none'}`}
              data={freqScatter}
              layout={plotLayout}
              onClick={(e) => {
                handlePlotMarkerClick(e.points[0]);
              }}
            />
          )}
        </div>
      </div>
      <table className='sensor-info-audio'>
        <thead>
          <th>{context.t('sample')}</th>
          <th>{context.t('clean_sample')}</th>
          <th>{context.t('radio')}</th>
          {audioSignal && <th>{context.t('signal')}</th>}
        </thead>
        <tbody>
          <tr>
            <td>{renderAudioElement(props.sampleAudio)}</td>
            <td>{renderAudioElement(props.cleanSampleUrl)}</td>
            <td>{renderAudioElement(props.radioAudio)}</td>
            {audioSignal && (
              <td>
                <Plot
                  data={[
                    {
                      x: audioSignal.map((x, i) => i),
                      y: audioSignal.map((x) => x / MAX_WAV_VALUE),
                      mode: 'lines',
                      type: 'scatter',
                      line: {
                        width: 1,
                      },
                    },
                  ]}
                  layout={{
                    height: 80,
                    width: 275,
                    margin: { t: 0, b: 0, l: 0, r: 0 },
                  }}
                  config={{}}
                />
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

SensorInfo.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default SensorInfo;
